import { Spinner } from '@client/components/_ui/feedback/Spinner';
import { theme as baseTheme, tokenValues } from '@client/design-system/__generated__';
import type { ThemeConfig } from 'antd';
import { theme as antdTheme, ConfigProvider } from 'antd';
import { type PropsWithChildren, useMemo } from 'react';

/**
 * The Ant Design theme for one colour scheme. Light is the generated base theme; dark merges the dark
 * algorithm with the dark design tokens.
 */
export function getAntdTheme(isDark: boolean): ThemeConfig {
    if (!isDark) {
        return baseTheme;
    }

    return {
        ...baseTheme,
        algorithm: antdTheme.darkAlgorithm,
        token: {
            ...baseTheme.token,
            // Dark mode color overrides for Ant Design (from design tokens)
            colorText: tokenValues.dark.semantic.foreground,
            colorTextSecondary: tokenValues.dark.semantic.mutedForeground,
            colorTextTertiary: tokenValues.dark.semantic.subtleForeground,
            colorTextQuaternary: tokenValues.dark.semantic.quaternaryForeground,
            colorTextPlaceholder: tokenValues.dark.semantic.placeholderForeground,
            // Every Ant text token set in the generator needs a dark counterpart here, or the
            // light value leaks into dark mode. scripts/check-contrast.mjs enforces the pairing.
            colorTextDescription: tokenValues.dark.semantic.subtleForeground,
            // accent.500 is 2.99:1 on the dark card and accent.600 only 4.38:1 on popovers. The dark
            // accent ramp inverts, so 700/800 are the lighter, AA-passing steps (CUR-1335).
            colorLink: tokenValues.dark.color.accent[700],
            colorLinkHover: tokenValues.dark.color.accent[800],
            colorLinkActive: tokenValues.dark.color.accent[800],
            colorBgContainer: tokenValues.dark.semantic.card,
            colorBgLayout: tokenValues.dark.semantic.background,
            colorBgElevated: tokenValues.dark.semantic.popover,
            colorBorder: tokenValues.dark.semantic.separator,
            colorBorderSecondary: tokenValues.dark.semantic.separatorSecondary,
        },
    };
}

/**
 * The app-wide `Spin` indicator, supplied through ConfigProvider rather than
 * `Spin.setDefaultIndicator`: the static default only took effect from an effect after the
 * first commit, so a page that mounted straight into a loading state (the hosted `/lodge`
 * pages, the root access check) kept Ant Design's dots until something re-rendered it.
 * Component config is inherited by the nested light-theme providers under `/public` and
 * `/lodge`, so every `Loading` renders the same `Spinner` from the first paint. `Spin` sizes it
 * from the `dotSize*` theme tokens. Its colour comes from the accent token rather than a baked
 * value, so a white-labelled public or hosted page — where `PublicThemeProvider` overrides
 * `--color-accent-500` with the insurer's colour — spins in its own brand instead of Curium's.
 */
export const antdSpinConfig = { indicator: <Spinner indicator /> };

/**
 * Curium's Ant Design theme without the app shell. The Claude Design `_ui` bundle (`.design-sync/`)
 * exports this so previews and designs render antd accents in Curium magenta rather than antd blue;
 * `UIProvider` is unusable there because its `App` wrapper stretches every card to the viewport.
 * Pass `dark` alongside `data-theme="dark"` on an ancestor.
 */
export function CuriumThemeProvider({ children, dark = false }: PropsWithChildren<{ dark?: boolean }>) {
    const theme = useMemo(() => getAntdTheme(dark), [dark]);

    return (
        <ConfigProvider theme={theme} spin={antdSpinConfig}>
            {children}
        </ConfigProvider>
    );
}
